import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  LoadingController,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import * as moment from "moment-timezone";
import {
  DiagnosisData,
  PRIMARY,
  RELATED,
  SECONDARY,
  UNRELATED,
} from "pages/patient-chart/diagnosis/models/diagnosis-data";
import { DiagnosisDTO } from "pages/patient-chart/diagnosis/models/diagnosis.dto";
import { StateManagementService } from "providers/state-management/state-management.service";
import { BehaviorSubject, Observable } from "rxjs";
import { take, tap } from "rxjs/operators";
import IcdCode from "../../../models/codes/IcdCode";
import ContentWrapper from "../../../models/content-wrapper/content-wrapper";
import Diagnosis, {
  PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE,
} from "../../../models/patient-chart/diagnosis";
import { DiagnosisService } from "../../../providers/patient-charts/diagnosis-service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";

@Component({
  selector: "patient-diagnosis-grid",
  templateUrl: "patient-diagnosis-grid.html",
})
export class PatientDiagnosisGridComponent implements OnInit, OnChanges {
  @Output() onCancel: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onSave: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() patientChartDetailsId: number;
  @Input() diagnosisDataList: Array<Diagnosis>;
  relatedActiveColor: string = "#AE2465";
  relatedInactiveColor: string = "grey";

  relatedlinkColor: string = this.relatedActiveColor;

  todaysDateAsISO: string = moment(new Date()).format("YYYY-MM-DD");
  diagnosisForm: FormGroup;
  diagnosisData: DiagnosisData;
  hideIcdCodeDropdownMenu: any = {
    primaryDiagnosis: true,
    secondaryDiagnosis: true,
  };
  spinner = null;
  spinnerCounter = 0;
  icdCodes: any = [];
  locations: any = [];
  hideRelatedDiagnosis = new Array(50).fill(true);
  primaryDiagnosisSelected: boolean = false;
  secondaryDiagnosisSelected: boolean = false;
  hasDuplicateDiagnosis: boolean = false;
  duplicateDiagnosis: any = [];
  diagnosisTypeBeingChanged: string;
  isRelatedDiagnosisValid: boolean = true;
  showUnrelatedDiagnosis: boolean = false;
  rnDiagnosisList: any = [];
  disableRelatedBtn: boolean = false;
  disableUnrelatedBtn: boolean = false;
  sortKeyCounter: number = 0;
  hasChanged: boolean = false;

  constructor(
    public navCtrl: NavController,
    public viewCtrl: ViewController,
    public navParams: NavParams,
    public _fb: FormBuilder,
    public referralService: ReferralsServiceProvider,
    private diagnosisService: DiagnosisService,
    public stateData: StateManagementService,
    public loadingCtrl: LoadingController
  ) {
    // initialize color
    this.setRelatedDiagnosisLinkColor(this.relatedActiveColor);
    this.getUnrelatedDiagnosisFlag.subscribe((value) => {
      this.showUnrelatedDiagnosis = value;
    });
  }

  protected get getUnrelatedDiagnosisFlag(): BehaviorSubject<boolean> {
    return this.stateData.showUnrelatedDiagnosis;
  }

  ngOnInit() {
    this.setup();
  }

  setup() {
    this.diagnosisData = {
      primary: null,
      secondary: null,
      related: [],
      unrelated: [],
    };
    this.rnDiagnosisList = [];
    this.diagnosisForm = new FormGroup({
      primaryDiagnosis: new FormControl(""),
      primaryDiagnosisStartDate: new FormControl(""),
      primaryDiagnosisEndDate: new FormControl(""),
      secondaryDiagnosis: new FormControl(""),
      secondaryDiagnosisStartDate: new FormControl(""),
      secondaryDiagnosisEndDate: new FormControl(""),
      relatedDiagnosisStartDate: new FormControl(""),
      relatedDiagnosisEndDate: new FormControl(""),
      languages: this._fb.array([]),
    });

    this.diagnosisForm.valueChanges.subscribe((value) => {
      const diagnosisArray: string[] = [];
      if (value.primaryDiagnosis) {
        diagnosisArray.push(value.primaryDiagnosis);
      }
      if (value.secondaryDiagnosis) {
        diagnosisArray.push(value.secondaryDiagnosis);
      }
      let tempArray = value.languages
        .map((related) => related.relatedDiagnosis)
        .filter((txt) => txt && txt.length > 0);
      tempArray.forEach((element) => {
        diagnosisArray.push(element);
      });
      this.hasDuplicateDiagnosis = this.hasDuplicatesInArray(diagnosisArray);
    });

    if (this.diagnosisDataList) {
      this.setExistingData(this.diagnosisDataList);
      this.setCountForHideRelatedDiagnosis();
      this.formatReletedUnreletedList();
      this.setFormControlsForEdit(this.diagnosisData);
    }
    this.setValidator();
    this.hasChanged = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.diagnosisDataList && changes.diagnosisDataList.previousValue) {
      this.setup();
      this.hasChanged = false;
    }
  }

  setExistingData(diagnosisList: Array<Diagnosis>) {
    diagnosisList.forEach((diagnosis) => {
      this.setExistingDataToMainObject(JSON.parse(JSON.stringify(diagnosis)));
    });

    this.sortRUDiagnosisList();
  }

  sortRUDiagnosisList() {
    if (this.diagnosisData.related.length > 1) {
      this.diagnosisData.related.sort((r1, r2) => r1.id - r2.id);
    }
    if (this.showUnrelatedDiagnosis) {
      if (this.diagnosisData.unrelated.length > 1) {
        this.diagnosisData.unrelated.sort((r1, r2) => r1.id - r2.id);
      }
    }
  }

  setExistingDataToMainObject(diagnosis: Diagnosis) {
    if (diagnosis == null) return;
    const type = diagnosis.diagnosisType;

    if (type === "RELATED") {
      if (this.diagnosisData.related === null) {
        this.diagnosisData.related = [];
      }

      if (this.containsDiagnosisValues(diagnosis)) {
        this.diagnosisData.related.push(diagnosis);
      }
    } else if (this.showUnrelatedDiagnosis && type === "UNRELATED") {
      if (this.diagnosisData.unrelated === null) {
        this.diagnosisData.unrelated = [];
      }

      if (this.containsDiagnosisValues(diagnosis)) {
        this.diagnosisData.unrelated.push(diagnosis);
      }
    } else {
      this.diagnosisData[type.toLowerCase()] = diagnosis;
    }
  }

  containsDiagnosisValues(diagnosis: Diagnosis): boolean {
    if (diagnosis.startDate !== null && diagnosis.icdCode.code !== null) {
      return true;
    } else {
      return false;
    }
  }

  setCountForHideRelatedDiagnosis() {
    if (this.hasRelatedDiagnosis) {
      let finalCount = 0;
      let relatedCount = this.diagnosisData.related.length;
      if (this.showUnrelatedDiagnosis) {
        finalCount = (relatedCount > 25 ? relatedCount : 25) + 25;
      } else {
        finalCount = relatedCount > 25 ? relatedCount : 25;
      }
      this.hideRelatedDiagnosis = new Array(finalCount).fill(true);
    } else {
      this.hideRelatedDiagnosis = new Array(50).fill(true);
    }
  }

  formatReletedUnreletedList() {
    if (this.showUnrelatedDiagnosis) {
      if (this.hasRelatedDiagnosis()) {
        this.rnDiagnosisList.push(
          ...this.diagnosisData.related.map((d) => Object.assign({}, d))
        );
      }
      if (this.hasUnrelatedDiagnosis()) {
        this.rnDiagnosisList.push(
          ...this.diagnosisData.unrelated.map((d) => Object.assign({}, d))
        );
      }
      if (this.rnDiagnosisList.length) {
        this.rnDiagnosisList.forEach((d) => {
          d.sortKey = null;
          if (d.diagnosisType === "RELATED") {
            d.diagnosisType = "related";
          } else {
            d.diagnosisType = "unrelated";
          }
        });
      }
    } else {
      if (this.hasRelatedDiagnosis()) {
        this.diagnosisData.related.forEach((d) => {
          const diagnosis = Object.assign({}, d);
          diagnosis.sortKey = null;
          diagnosis.diagnosisType = "related";
          this.rnDiagnosisList.push(diagnosis);
        });
      }
    }
  }

  public hasPrimaryDiagnosisDescription(): boolean {
    return !!this.diagnosisForm.controls.primaryDiagnosis.value;
  }

  private hasDuplicatesInArray(stringArray: string[]) {
    const counts = [];
    for (let i = 0; i <= stringArray.length; i++) {
      if (counts[stringArray[i]] === undefined) {
        counts[stringArray[i]] = 1;
        this.duplicateDiagnosis = [];
      } else {
        this.duplicateDiagnosis = stringArray.filter(
          (item, index) => stringArray.indexOf(item) !== index
        );
        return true;
      }
    }
    return false;
  }

  isDiagnosisDuplicate(index: number, dType: string): boolean {
    if (dType === "primaryDiagnosis" || dType === "secondaryDiagnosis") {
      return this.duplicateDiagnosis.includes(
        this.diagnosisForm.controls[dType].value
      );
    } else {
      return this.duplicateDiagnosis.includes(
        this.diagnosisForm.controls.languages.value[index].relatedDiagnosis
      );
    }
  }

  /**
   * It will set the data of the primary secondary and related diagnosis
   * when user wants to edit the data of diagnosis.
   * @param diagnosisData
   */
  setData(diagnosisData: Array<Diagnosis>) {
    this.diagnosisData.related = [];
    this.diagnosisData.unrelated = [];
    let index = 0;

    diagnosisData.forEach((diagnosis, key) => {
      if (diagnosis.diagnosisType === PRIMARY) {
        this.primaryDiagnosisSelected = true;
        this.setDataToMainObject(diagnosis, PRIMARY);
      } else if (diagnosis.diagnosisType === SECONDARY) {
        this.secondaryDiagnosisSelected = true;
        this.setDataToMainObject(diagnosis, SECONDARY);
      } else if (diagnosis.diagnosisType === RELATED) {
        this.setDataToMainObject(diagnosis, RELATED, index);
        index++;
        this.addRelatedDiagnosisInput(null, 0);
      } else if (diagnosis.diagnosisType === UNRELATED) {
        this.setDataToMainObject(diagnosis, UNRELATED, index);
        index++;
        this.addRelatedDiagnosisInput(null, 0);
      }
    });

    const control = <FormArray>this.diagnosisForm.controls["languages"];
    if (
      diagnosisData.length === 1 ||
      this.diagnosisData.related.length === 0 ||
      this.diagnosisData.unrelated.length === 0 ||
      control.length === 0
    ) {
      this.addRelatedDiagnosisInput(null, 0);
    }
  }

  setFormControlsForEdit(diagnosisData: DiagnosisData) {
    if (this.hasPrimary()) {
      this.diagnosisForm.controls["primaryDiagnosis"].setValue(
        `${diagnosisData.primary.icdCode.description} (${diagnosisData.primary.icdCode.code})`
      );
      if (diagnosisData.primary.startDate !== null) {
        this.diagnosisForm.controls["primaryDiagnosisStartDate"].setValue(
          `${this.getISOFormattedDate(diagnosisData.primary.startDate)}`
        );
      }
      if (diagnosisData.primary.endDate !== null) {
        this.diagnosisForm.controls["primaryDiagnosisEndDate"].setValue(
          `${this.getISOFormattedDate(diagnosisData.primary.endDate)}`
        );
      }
    }

    if (this.hasSecondary()) {
      this.diagnosisForm.controls["secondaryDiagnosis"].setValue(
        `${diagnosisData.secondary.icdCode.description} (${diagnosisData.secondary.icdCode.code})`
      );
      if (diagnosisData.secondary.startDate !== null) {
        this.diagnosisForm.controls["secondaryDiagnosisStartDate"].setValue(
          `${this.getISOFormattedDate(diagnosisData.secondary.startDate)}`
        );
      }
      if (diagnosisData.secondary.endDate !== null) {
        this.diagnosisForm.controls["secondaryDiagnosisEndDate"].setValue(
          `${this.getISOFormattedDate(diagnosisData.secondary.endDate)}`
        );
      }
    }

    if (this.hasRelatedDiagnosis()) {
      diagnosisData.related.forEach((diagnosis: Diagnosis, index: number) => {
        this.addRelatedDiagnosisInput(diagnosis, index);
      });
    }

    if (this.showUnrelatedDiagnosis) {
      if (this.hasUnrelatedDiagnosis()) {
        diagnosisData.unrelated.forEach(
          (diagnosis: Diagnosis, index: number) => {
            this.addRelatedDiagnosisInput(diagnosis, index);
          }
        );
      }
    }
  }

  hasDiagnosisData(): boolean {
    return (
      this.hasPrimary() ||
      this.hasSecondary() ||
      this.hasRelatedDiagnosis() ||
      this.hasUnrelatedDiagnosis()
    );
  }

  hasPrimary(): boolean {
    return this.diagnosisData && this.diagnosisData.primary !== null;
  }

  hasSecondary(): boolean {
    return this.diagnosisData && this.diagnosisData.secondary !== null;
  }

  getRelatedDiagnosis(): Array<Diagnosis> {
    if (this.hasRelatedDiagnosis()) {
      return this.diagnosisData.related;
    } else {
      return [];
    }
  }

  getUnrelatedDiagnosis(): Array<Diagnosis> {
    if (this.hasUnrelatedDiagnosis()) {
      return this.diagnosisData.unrelated;
    } else {
      return [];
    }
  }

  hasRelatedDiagnosis(): boolean {
    return (
      this.diagnosisData &&
      this.diagnosisData.related !== null &&
      this.diagnosisData.related.length > 0
    );
  }

  hasUnrelatedDiagnosis(): boolean {
    return (
      this.diagnosisData &&
      this.diagnosisData.unrelated !== undefined &&
      this.diagnosisData.unrelated !== null &&
      this.diagnosisData.unrelated.length > 0
    );
  }

  setDiagnosisTypeBeingChanged(diagnosisType: string) {
    this.diagnosisTypeBeingChanged = diagnosisType;
  }

  getDiagnosisTypeBeingChanged() {
    return this.diagnosisTypeBeingChanged;
  }

  isPrimarySectionBeingChanged(): boolean {
    return this.diagnosisTypeBeingChanged === PRIMARY;
  }

  isSecondarySectionBeingChanged(): boolean {
    return this.diagnosisTypeBeingChanged === SECONDARY;
  }

  isRelatedSectionBeingChanged(): boolean {
    return this.diagnosisTypeBeingChanged === RELATED;
  }

  setDataToMainObject(diagnosis: Diagnosis, type: string, index: number = 0) {
    if (type == RELATED) {
      this.diagnosisData.related[index] = diagnosis;
    } else if (type == UNRELATED) {
      this.diagnosisData.unrelated[index] = diagnosis;
    } else {
      this.diagnosisData[type] = diagnosis;
    }
  }

  /**
   *
   * @param searchTerm user enters to search the disease, when the term length is >= 3 at that time call that getIcdCodes api
   * @param diagnosisType is it primary or secondary
   */
  searchDiagnosis(event: any, searchTerm: string, diagnosisType: string) {
    if (searchTerm) {
      if (diagnosisType === "primaryDiagnosis") {
        this.primaryDiagnosisSelected = false;
        this.setDiagnosisTypeBeingChanged(PRIMARY);
        if (searchTerm.length > 0 && searchTerm !== "") {
          if (searchTerm.length >= 3) {
            this.getIcdCodes(searchTerm);
          } else {
            this.icdCodes = this.locations;
          }

          this.diagnosisForm.controls["primaryDiagnosis"].setErrors({
            incorrect: true,
          });
        } else {
          this.diagnosisForm.controls["primaryDiagnosis"].setErrors(null);
        }
      }

      if (diagnosisType === "secondaryDiagnosis") {
        this.secondaryDiagnosisSelected = false;
        this.setDiagnosisTypeBeingChanged(SECONDARY);
        if (searchTerm.length > 0 && searchTerm !== "") {
          this.diagnosisForm.controls["secondaryDiagnosis"].setErrors({
            incorrect: true,
          });
        } else {
          this.diagnosisForm.controls["secondaryDiagnosis"].setErrors(null);
        }
      }

      if (
        this.icdCodes.filter((issue) => issue.name === searchTerm).length ===
          0 &&
        searchTerm !== ""
      ) {
        this.hideIcdCodeDropdownMenu[diagnosisType] = false;
        if (searchTerm.length >= 3) {
          this.getIcdCodes(searchTerm);
        } else {
          this.icdCodes = this.locations;
        }
      }
    } else {
      if (diagnosisType === "primaryDiagnosis") {
        this.setDiagnosisTypeBeingChanged(PRIMARY);
      } else if (diagnosisType === "secondaryDiagnosis") {
        this.clearSecondaryValidator();
        this.setDiagnosisTypeBeingChanged(SECONDARY);
      } else {
        this.setDiagnosisTypeBeingChanged(RELATED);
      }
    }
    if (event.isFocus()) {
      this.hasChanged = true;
    } else {
      this.hideIcdCodeDropdownMenu["primaryDiagnosis"] = true;
      this.hideIcdCodeDropdownMenu["secondaryDiagnosis"] = true;
    }
  }

  /**
   *
   * @param icdCode selected diagnosis from the drop down menu bar
   * @param control is the control is primary or secondary diagnosis
   * set the selected code in diagnosisiCodeData
   */
  setDiagnosis(icdCode: IcdCode, control: string) {
    this.setDiagnosisTypeBeingChanged(null);

    this.diagnosisForm.controls[control].setValue(
      `${icdCode.description} (${icdCode.code})`
    );
    this.hideIcdCodeDropdownMenu[control] = true;
    if (control === "primaryDiagnosis") {
      this.diagnosisData.primary = {
        diagnosisType: PRIMARY,
        endDate: undefined,
        id: 0,
        patientChartId: 0,
        physician: "",
        physicianId: 0,
        startDate: undefined,
        updatedBy: "",
        icdCode: {
          id: icdCode.id,
          code: icdCode.code,
          description: icdCode.description,
          grouping: icdCode.grouping,
        },
        comment: "",
      };
      this.primaryDiagnosisSelected = true;
      this.diagnosisForm.controls["primaryDiagnosis"].setErrors(null);
    }
    if (control === "secondaryDiagnosis") {
      this.diagnosisData.secondary = {
        diagnosisType: SECONDARY,
        endDate: undefined,
        id: 0,
        patientChartId: 0,
        physician: "",
        physicianId: 0,
        startDate: undefined,
        updatedBy: "",
        icdCode: {
          id: icdCode.id,
          code: icdCode.code,
          description: icdCode.description,
          grouping: icdCode.grouping,
        },
        comment: "",
      };
      this.secondaryDiagnosisSelected = true;
      this.diagnosisForm.controls["secondaryDiagnosis"].setErrors(null);
    }
    this.setValidator();
    this.hasChanged = true;
  }

  setValidator() {
    this.diagnosisForm
      .get("primaryDiagnosis")
      .setValidators([Validators.required]);
  }

  clearPrimaryValidator() {
    this.diagnosisForm.controls["primaryDiagnosis"].setValue(null);
    this.diagnosisForm.controls["primaryDiagnosisStartDate"].setValue(null);
    this.diagnosisForm.controls["primaryDiagnosisEndDate"].setValue(null);

    if (this.diagnosisData.primary) {
      this.diagnosisData.primary.icdCode = {
        description: null,
        code: null,
        id: null,
        grouping: null,
      };
      this.diagnosisData.primary.startDate = null;
      this.diagnosisData.primary.endDate = null;
    }
  }

  clearSecondaryValidator() {
    this.diagnosisForm.get("secondaryDiagnosisStartDate").clearValidators();

    this.diagnosisForm
      .get("secondaryDiagnosisStartDate")
      .updateValueAndValidity();

    this.diagnosisForm.controls["secondaryDiagnosis"].setValue(null);
    this.diagnosisForm.controls["secondaryDiagnosisStartDate"].setValue(null);
    this.diagnosisForm.controls["secondaryDiagnosisEndDate"].setValue(null);

    if (this.diagnosisData.secondary) {
      this.diagnosisData.secondary.icdCode = {
        description: null,
        code: null,
        id: null,
        grouping: null,
      };
      this.diagnosisData.secondary.startDate = null;
      this.diagnosisData.secondary.endDate = null;
    }
  }

  isFormValid(): boolean {
    this.diagnosisForm.controls["primaryDiagnosis"].value;
    return this.diagnosisForm.valid && !this.hasDuplicateDiagnosis;
  }

  getIsRelatedDiagnosisValid(): boolean {
    return this.isRelatedDiagnosisValid;
  }

  /**
   *
   * @param searchTerm user enters to search the disease, when the term length is >= 3 at that time call that getIcdCodes api
   * @param index
   */
  searchRelatedDiagnosis(searchTerm: string, index) {
    if (searchTerm.length > 0 && searchTerm !== "") {
      this.diagnosisForm.controls.languages["controls"][
        index
      ].controls.relatedDiagnosis.setErrors({
        incorrect: true,
      });
    } else {
      this.diagnosisForm.controls.languages["controls"][
        index
      ].controls.relatedDiagnosis.setErrors(null);
    }

    if (
      this.icdCodes.filter((issue) => issue.name === searchTerm).length === 0 &&
      searchTerm !== ""
    ) {
      this.hideRelatedDiagnosis[index] = false;

      if (searchTerm.length >= 3) {
        this.setDiagnosisTypeBeingChanged(RELATED);
        this.getIcdCodes(searchTerm);
      } else {
        this.icdCodes = this.locations;
      }
    } else {
      this.clearRelatedDiagnosisData(index);
    }
    this.updateRelatedDiagnosisLinkColor();
    this.hasChanged = true;
  }

  /**
   *
   * @param diagnosis selected diagnosis from the drop down menu bar
   * @param control is the control is primary or secondary diagnosis
   * set the selected code in diagnosisiCodeData
   */

  clearRelatedDiagnosisData(index) {
    this.diagnosisForm.controls.languages["controls"][
      index
    ].controls.relatedDiagnosis.setValue(null);
    let obj = {
      diagnosisType: this.showUnrelatedDiagnosis
        ? this.rnDiagnosisList[index].diagnosisType
        : RELATED,
      endDate: undefined,
      id: this.getIdByIndex(index),
      patientChartId: 0,
      physician: "",
      physicianId: 0,
      startDate: undefined,
      updatedBy: "",
      sortKey: this.getSortKeyByIndex(index),
      icdCode: {
        id: null,
        code: null,
        description: null,
        grouping: null,
      },
      comment: "",
    };
    if (this.showUnrelatedDiagnosis) {
      this.rnDiagnosisList[index].icdCode = {
        id: null,
        code: null,
        description: null,
        grouping: null,
      };
    } else {
      this.diagnosisData.related[index].icdCode = {
        id: null,
        code: null,
        description: null,
        grouping: null,
      };
    }
  }

  /**
   *
   * @param icdCode selected diagnosis from the drop down menu bar
   * @param index where should set related diagnosis as per index
   * set the selected code in diagnosisiCodeData.related array
   */
  setRelatedDiagnosis(icdCode: any, index) {
    this.diagnosisForm.controls.languages["controls"][
      index
    ].controls.relatedDiagnosis.setValue(
      `${icdCode.description} (${icdCode.code})`
    );
    this.hideRelatedDiagnosis[index] = true;
    this.diagnosisForm.controls.languages["controls"][
      index
    ].controls.relatedDiagnosis.setErrors(null);

    let obj = {
      diagnosisType: this.showUnrelatedDiagnosis
        ? this.rnDiagnosisList[index].diagnosisType
        : RELATED,
      endDate: undefined,
      id: this.getIdByIndex(index),
      patientChartId: 0,
      physician: "",
      physicianId: 0,
      startDate: undefined,
      updatedBy: "",
      sortKey: this.getSortKeyByIndex(index),
      icdCode: {
        id: icdCode.id,
        code: icdCode.code,
        description: icdCode.description,
        grouping: icdCode.grouping,
      },
      comment: "",
    };

    if (this.showUnrelatedDiagnosis) {
      this.rnDiagnosisList[index] = obj;
    } else {
      this.diagnosisData.related[index] = obj;
    }

    this.updateRelatedDiagnosisLinkColor();
    this.hasChanged = true;
  }

  getIdByIndex(index: number): number {
    if (this.showUnrelatedDiagnosis) {
      return this.rnDiagnosisList[index].id;
    } else {
      return this.diagnosisData.related[index].id;
    }
  }

  getSortKeyByIndex(index: number): number {
    if (this.showUnrelatedDiagnosis) {
      return this.rnDiagnosisList[index].sortKey;
    } else {
      return this.diagnosisData.related[index].sortKey;
    }
  }

  /**
   * initiate the related diagnosis input form control
   */
  initRelatedDiagnosis(relatedDiagnosisObj: Diagnosis) {
    let diagnosisDescription = "";

    if (relatedDiagnosisObj.icdCode && relatedDiagnosisObj.icdCode.code) {
      diagnosisDescription = `${relatedDiagnosisObj.icdCode.description} (${relatedDiagnosisObj.icdCode.code})`;
    }

    return this._fb.group({ relatedDiagnosis: [diagnosisDescription] });
  }

  /**
   * When user add related diagnosis input formcontrol;
   * adding sortKeyCounter to save diagnoses in same order as they are added.
   */
  addRelatedDiagnosisInput(
    relatedDiagnosisObj: Diagnosis = null,
    index: number = null
  ) {
    const control = <FormArray>this.diagnosisForm.controls["languages"];
    //if related diagnosis object not created, then create one
    if (!relatedDiagnosisObj) {
      relatedDiagnosisObj = {
        diagnosisType: this.disableRelatedBtn ? UNRELATED : RELATED,
        id: 0,
        patientChartId: 0,
        physician: "",
        physicianId: 0,
        updatedBy: "",
        sortKey: this.getSortKey(),
        icdCode: {
          id: null,
          code: null,
          description: null,
          grouping: null,
        },
        startDate: null,
        endDate: null,
        comment: "",
      };

      if (this.showUnrelatedDiagnosis) {
        this.rnDiagnosisList.push(relatedDiagnosisObj);
      } else {
        this.diagnosisData.related.push(relatedDiagnosisObj);
      }
    }

    control.push(this.initRelatedDiagnosis(relatedDiagnosisObj));
    this.updateRelatedDiagnosisLinkColor();
  }

  getSortKey(): number {
    this.sortKeyCounter += 1;
    return this.sortKeyCounter;
  }

  previousRelatedDiagnosisDefined(
    relatedDiagnosisObj: Diagnosis = null
  ): boolean {
    // keeps current logic

    let previousDefined: boolean = true;

    if (this.showUnrelatedDiagnosis) {
      if (this.rnDiagnosisList.length > 0) {
        for (let i = 0; i < this.rnDiagnosisList.length; i++) {
          let diagnosisOption = this.rnDiagnosisList[i];
          let item: any = this.diagnosisForm.controls.languages.value[i];

          if (
            diagnosisOption === null ||
            diagnosisOption.icdCode === null ||
            diagnosisOption.icdCode.id === null ||
            item === null
          ) {
            previousDefined = false;
            break;
          }
        }
      }

      let relatedcount,
        unrelatedcount = 0;
      relatedcount = this.rnDiagnosisList.filter(
        (t) => t.diagnosisType === RELATED
      ).length;
      unrelatedcount = this.rnDiagnosisList.filter(
        (t) => t.diagnosisType === UNRELATED
      ).length;

      if (relatedcount >= 25) {
        this.disableRelatedBtn = true;
      } else {
        this.disableRelatedBtn = false;
      }

      if (unrelatedcount >= 25) {
        this.disableUnrelatedBtn = true;
      } else {
        this.disableUnrelatedBtn = false;
      }

      if (relatedcount >= 25 && unrelatedcount >= 25) {
        previousDefined = false;
      }

      return previousDefined;
    } else {
      const control = <FormArray>this.diagnosisForm.controls["languages"];

      if (this.diagnosisData.related.length > 0) {
        for (let i = 0; i < this.diagnosisData.related.length; i++) {
          let relatedDiagnosis = this.diagnosisData.related[i];
          let item: any = this.diagnosisForm.controls.languages.value[i];
          if (
            relatedDiagnosis === null ||
            relatedDiagnosis.icdCode === null ||
            relatedDiagnosis.icdCode.id === null ||
            item === null
          ) {
            previousDefined = false;
            break;
          }
        }
      }
      // Only allow up to total of 25
      if (control.length >= 25) {
        previousDefined = false;
      }
      return previousDefined;
    }
  }

  /**
   * remove selected related diagnosis input form control and remove it from the diagnosisiCodeData
   * @param index
   */
  removeRelatedDiagnosisInput(index: number) {
    if (this.showUnrelatedDiagnosis) {
      if (this.rnDiagnosisList.length > 0)
        this.rnDiagnosisList.splice(index, 1);
    } else {
      this.diagnosisData.related.splice(index, 1);
    }

    const control = <FormArray>this.diagnosisForm.controls["languages"];
    control.removeAt(index);

    this.updateRelatedDiagnosisLinkColor();
    this.hasChanged = true;
  }

  updateRelatedDiagnosisLinkColor() {
    if (this.previousRelatedDiagnosisDefined()) {
      this.setRelatedDiagnosisLinkColor(this.relatedActiveColor);
    } else {
      this.setRelatedDiagnosisLinkColor(this.relatedInactiveColor);
    }
  }

  showRequiredForDiagnosis(diagnosisObject: Diagnosis): boolean {
    return (
      diagnosisObject &&
      diagnosisObject.icdCode &&
      !!diagnosisObject.icdCode.description
    );
  }

  getItemToCheckRequired(index: number): Diagnosis {
    if (this.showUnrelatedDiagnosis) {
      return this.rnDiagnosisList[index];
    } else {
      return this.diagnosisData.related[index];
    }
  }

  /**
   *
   * @param searchTerm search term to get icd-codes
   * get icd code and code description as per search tem entered by user
   */
  getIcdCodes(searchTerm) {
    this.referralService
      .getIcdCodeSearch(searchTerm)
      .pipe(take(1))
      .subscribe((response: ContentWrapper<IcdCode>) => {
        // this.locations = response;
        if (
          this.stateData.patientCareType &&
          this.stateData.patientCareType.toLowerCase() === "palliative"
        ) {
          this.icdCodes = response.content.filter(
            (e) => e.code != PALLIATIVE_ENCOUNTER_DIAGNOSIS_CODE
          );
        } else {
          this.icdCodes = response.content;
        }
      });
  }

  /**
   *on submit click it will send data to the server
   */
  onSubmit() {
    this.increaseSpinnerCount();
    if (
      this.diagnosisData.secondary &&
      !this.diagnosisData.secondary.icdCode.code
    ) {
      this.diagnosisData.secondary = null;
    }

    if (this.showUnrelatedDiagnosis) {
      if (this.rnDiagnosisList.length > 0) {
        this.rnDiagnosisList = this.rnDiagnosisList.filter(
          (d) => d.icdCode.code !== null
        );
      }
    } else {
      if (this.diagnosisData.related.length > 0) {
        this.diagnosisData.related = this.diagnosisData.related.filter(
          (r) => r.icdCode.code !== null
        );
      }
    }

    let diagnosisDataSendToServer;
    diagnosisDataSendToServer = this.setFormattedDateToMainObjectForReferals();

    const diagnoses: Diagnosis[] = this.convertDataSendServer(
      diagnosisDataSendToServer
    );
    this.sendDiagnosisDataToServer(diagnoses)
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.decreaseSpinnerCount();
          this.hasChanged = false;
        },
        (err) => {
          this.decreaseSpinnerCount();
          if (err.error) {
            this.referralService.presentToast(err.error.errorMessage);
          }
        }
      );
  }

  /**
   * for both: create and edit functionalities, PUT api will be used
   */
  sendDiagnosisDataToServer(diagnosisDataSendToServer): Observable<any> {
    return this.diagnosisService
      .putDiagnosisData(this.patientChartDetailsId, diagnosisDataSendToServer)
      .pipe(
        take(1),
        tap((_) => {
          this.diagnosisService.getDiagnosisData(this.patientChartDetailsId);
          this.onSave.emit();
        })
      );
  }

  getTodaysDate(): string {
    return this.todaysDateAsISO;
  }

  setFormattedDateToMainObjectForReferals(): DiagnosisDTO {
    let diagnosisDataSendToServer = JSON.parse(
      JSON.stringify(this.diagnosisData)
    );

    if (this.diagnosisData.primary) {
      diagnosisDataSendToServer.primary.startDate = this.diagnosisData.primary
        .startDate
        ? this.getFormattedDate(this.diagnosisData.primary.startDate)
        : this.getFormattedDate(this.getTodaysDate());
      diagnosisDataSendToServer.primary.endDate = null;
    }

    if (this.diagnosisData.secondary) {
      diagnosisDataSendToServer.secondary.startDate = this.diagnosisData
        .secondary.startDate
        ? this.getFormattedDate(this.diagnosisData.secondary.startDate)
        : this.getFormattedDate(this.getTodaysDate());
      diagnosisDataSendToServer.secondary.endDate = null;
    }

    if (this.showUnrelatedDiagnosis) {
      if (this.rnDiagnosisList.length > 0) {
        this.rnDiagnosisList.map((diagnosis, index) => {
          this.rnDiagnosisList[index].startDate = this.rnDiagnosisList[index]
            .startDate
            ? this.getFormattedDate(this.rnDiagnosisList[index].startDate)
            : this.getFormattedDate(this.getTodaysDate());

          this.rnDiagnosisList[index].endDate = null;
        });
      }
    } else {
      if (this.diagnosisData.related.length > 0) {
        this.diagnosisData.related.map((diagnosis, index) => {
          diagnosisDataSendToServer.related[index].startDate = this
            .diagnosisData.related[index].startDate
            ? this.getFormattedDate(this.diagnosisData.related[index].startDate)
            : this.getFormattedDate(this.getTodaysDate());

          diagnosisDataSendToServer.related[index].endDate = null;
        });
      }
    }
    return diagnosisDataSendToServer;
  }
  /*
    converting data to appropriate structure for sending to the server
   */
  convertDataSendServer(diagnosisDataSendToServer): Diagnosis[] {
    const diagnoses: Diagnosis[] = [];
    let relatedList: any = [];
    let unrelatedList: any = [];

    let primary = diagnosisDataSendToServer.primary;
    let secondary = diagnosisDataSendToServer.secondary;

    if (this.showUnrelatedDiagnosis) {
      relatedList = JSON.parse(
        JSON.stringify(
          this.rnDiagnosisList.filter((t) => t.diagnosisType === RELATED)
        )
      );
      unrelatedList = JSON.parse(
        JSON.stringify(
          this.rnDiagnosisList.filter((t) => t.diagnosisType === UNRELATED)
        )
      );
    } else {
      relatedList = diagnosisDataSendToServer.related;
    }

    // setting params for primary diagnosis
    const primaryIcdCode = new IcdCode(
      primary.icdCode.id,
      primary.icdCode.code,
      primary.icdCode.description,
      primary.icdCode.grouping
    );
    const primaryDiagnosis = new Diagnosis();
    primaryDiagnosis.diagnosisType = PRIMARY;
    primaryDiagnosis.icdCode = primaryIcdCode;
    primaryDiagnosis.startDate = primary.startDate;
    primaryDiagnosis.endDate = primary.endDate;
    primaryDiagnosis.physicianId = primary.physicianId;
    primaryDiagnosis.id = primary.id;
    primaryDiagnosis.sortKey = null;
    diagnoses.push(primaryDiagnosis);

    // setting params for secondary diagnosis
    if (secondary !== null) {
      const secondaryIcdCode = new IcdCode(
        secondary.icdCode.id,
        secondary.icdCode.code,
        secondary.icdCode.description,
        secondary.icdCode.grouping
      );
      const secondaryDiagnosis = new Diagnosis();
      secondaryDiagnosis.diagnosisType = SECONDARY;
      secondaryDiagnosis.icdCode = secondaryIcdCode;
      secondaryDiagnosis.startDate = secondary.startDate;
      secondaryDiagnosis.endDate = secondary.endDate;
      secondaryDiagnosis.physicianId = secondary.physicianId;
      secondaryDiagnosis.id = secondary.id;
      secondaryDiagnosis.sortKey = null;
      diagnoses.push(secondaryDiagnosis);
    }

    // setting params for related diagnosis
    if (relatedList.length > 0) {
      for (let i = 0; i < relatedList.length; i++) {
        const item: Diagnosis = relatedList[i];
        const relatedIcdCode = new IcdCode(
          item.icdCode.id,
          item.icdCode.code,
          item.icdCode.description,
          item.icdCode.grouping
        );
        const relatedDiagnosis = new Diagnosis();
        relatedDiagnosis.diagnosisType = RELATED;
        relatedDiagnosis.icdCode = relatedIcdCode;
        relatedDiagnosis.startDate = relatedList[i].startDate;
        relatedDiagnosis.endDate = relatedList[i].endDate;
        relatedDiagnosis.physicianId = relatedList[i].physicianId;
        relatedDiagnosis.id = relatedList[i].id;
        relatedDiagnosis.sortKey = relatedList[i].sortKey;
        diagnoses.push(relatedDiagnosis);
      }
    }

    // setting params for unrelated diagnosis
    if (this.showUnrelatedDiagnosis && unrelatedList.length > 0) {
      for (let i = 0; i < unrelatedList.length; i++) {
        const item: Diagnosis = unrelatedList[i];
        const unrelatedIcdCode = new IcdCode(
          item.icdCode.id,
          item.icdCode.code,
          item.icdCode.description,
          item.icdCode.grouping
        );
        const unrelatedDiagnosis = new Diagnosis();
        unrelatedDiagnosis.diagnosisType = UNRELATED;
        unrelatedDiagnosis.icdCode = unrelatedIcdCode;
        unrelatedDiagnosis.startDate = unrelatedList[i].startDate;
        unrelatedDiagnosis.endDate = unrelatedList[i].endDate;
        unrelatedDiagnosis.physicianId = unrelatedList[i].physicianId;
        unrelatedDiagnosis.id = unrelatedList[i].id;
        unrelatedDiagnosis.sortKey = unrelatedList[i].sortKey;
        diagnoses.push(unrelatedDiagnosis);
      }
    }
    return diagnoses;
  }

  /**
   * get formatted date
   * @param date
   */
  getFormattedDate(date) {
    if (date) {
      let dateObj = date.split("T");
      return moment(dateObj[0]).format("MM/DD/YYYY hh:mm:ss");
    }
    return null;
  }

  /**
   * get formatted date
   * @param date
   */
  getISOFormattedDate(date) {
    if (date) {
      let dateObj = moment.utc(date).toDate();
      dateObj = moment(dateObj).local();
      dateObj = moment(dateObj).format("MM/DD/YYYY hh:mm:ss");
      return moment(dateObj, "MM/DD/YYYY hh:mm:ss").toISOString();
    }
    return null;
  }

  showDatesForDiagnosis(): boolean {
    return (
      this.referralService.PATIENT_STATUS === "patient" ||
      this.referralService.PATIENT_STATUS === "discharge"
    );
  }

  setRelatedDiagnosisLinkColor(linkColor: string) {
    document.documentElement.style.setProperty(
      "--relatedlink-color",
      linkColor
    );
  }

  /**
   * on changing radio button, make its id=0 if its greater than 0,
   * means its already exist in DB, so we want to create new record
   * for updated diagnosis by making its id=0.
   */
  relSelection(type: string, index: number) {
    let item = this.rnDiagnosisList[index];
    if (item.diagnosisType !== type) {
      this.rnDiagnosisList[index].diagnosisType = type;
      if (item.id > 0) {
        this.rnDiagnosisList[index].id = 0;
        this.rnDiagnosisList[index].sortKey = this.getSortKey();
      }
      this.previousRelatedDiagnosisDefined();
      this.updateRelatedDiagnosisLinkColor();
      this.hasChanged = true;
    }
  }

  getRUDiagnosisType(index) {
    if (this.showUnrelatedDiagnosis) {
      if (this.rnDiagnosisList[index].diagnosisType === RELATED)
        return "Related Diagnosis";
      else return "Unrelated Diagnosis";
    } else {
      return "Related Diagnosis";
    }
  }

  showNoResultFound() {
    if (this.showUnrelatedDiagnosis) {
      if (this.icdCodes.length === 0 && this.rnDiagnosisList) return true;
      else return false;
    } else {
      if (this.icdCodes.length === 0 && this.diagnosisData.related) return true;
      else return false;
    }
  }

  disableRelatedSearch(index: number): boolean {
    if (this.showUnrelatedDiagnosis) {
      return this.rnDiagnosisList[index].id > 0;
    } else {
      return this.diagnosisData.related[index].id > 0;
    }
  }

  onDismiss() {
    if (this.hasChanged) {
      this.onCancel.emit(true);
      this.hasChanged = false;
    } else {
      const relatedUnrelatedControls = <FormArray>(
        this.diagnosisForm.controls["languages"]
      );
      for (let i = 0; i < relatedUnrelatedControls.controls.length; i++) {
        this.removeRelatedDiagnosisInput(i);
      }
      this.diagnosisForm.reset();
    }
  }

  increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/intake-queue/patient-diagnosis-grid/patient-diagnosis-grid.ts